import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api, { API } from "@/lib/api";
import { currentMonth, monthLabel } from "@/lib/format";
import { PageHeader } from "@/components/common/Page";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { FileText, Download, FileSpreadsheet, Loader2 } from "lucide-react";

export default function Reports() {
  const [month, setMonth] = useState(currentMonth());
  const [downloading, setDownloading] = useState(null);
  const { data } = useQuery({ queryKey: ["report-types"], queryFn: async () => (await api.get("/reports/types")).data });

  const download = async (typeId, fmt) => {
    setDownloading(`${typeId}-${fmt}`);
    try {
      const res = await api.get(`/reports/${typeId}?format=${fmt}&month=${month}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${typeId}-${month}.${fmt === "excel" ? "xlsx" : fmt}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Report downloaded");
    } catch (e) {
      toast.error("Could not generate report");
    } finally {
      setDownloading(null);
    }
  };

  return (
    <div className="ns-fade">
      <PageHeader title="Reports" subtitle="Export management reports — CSV for analysis, branded PDF for records" testId="reports-header">
        <div className="flex items-center gap-2">
          <Label className="text-xs text-slate-400">Period</Label>
          <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="w-40 border-[#2D3F62] bg-[#0B1120] text-slate-100" data-testid="report-month-input" />
        </div>
      </PageHeader>

      <div className="mb-4 text-sm text-slate-500">Reporting on <span className="text-slate-300">{monthLabel(month)}</span></div>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        {(data?.types || []).map((t, i) => (
          <div key={t.id} className="ns-rise flex items-center justify-between rounded-xl border border-[#1E2D4A] bg-[#17233B]/60 p-4 transition-colors hover:border-[#2D3F62]" style={{ animationDelay: `${i * 30}ms` }} data-testid={`report-row-${t.id}`}>
            <div className="flex items-center gap-3">
              <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-cyan-500/10"><FileText className="h-4 w-4 text-cyan-400" /></div>
              <span className="font-medium text-slate-200">{t.name}</span>
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" className="border-[#2D3F62] bg-transparent text-slate-300 hover:bg-[#1E2D4A]" onClick={() => download(t.id, "csv")} disabled={downloading === `${t.id}-csv`} data-testid={`export-csv-${t.id}`}>
                {downloading === `${t.id}-csv` ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><FileSpreadsheet className="mr-1 h-3.5 w-3.5" /> CSV</>}
              </Button>
              <Button size="sm" className="bg-gradient-to-r from-cyan-500 to-sky-600 font-semibold text-slate-900 hover:from-cyan-400 hover:to-sky-500" onClick={() => download(t.id, "pdf")} disabled={downloading === `${t.id}-pdf`} data-testid={`export-pdf-${t.id}`}>
                {downloading === `${t.id}-pdf` ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Download className="mr-1 h-3.5 w-3.5" /> PDF</>}
              </Button>
            </div>
          </div>
        ))}
      </div>
      <p className="mt-6 text-[11px] italic text-slate-600">Management reporting only — not a lodged BAS. GST calculated at 10% per ATO rules to support BAS preparation. Confirm statutory obligations with NavScora's registered Australian accountant/BAS agent.</p>
      <p className="mt-2 text-[10px] text-slate-700">NavScora Admin™ is a trademark of Navscora Pty Ltd. All rights reserved.</p>
    </div>
  );
}
